import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { 
  Card, 
  CardContent, 
  Typography, 
  Rating, 
  Badge, 
  Avatar, 
  Box, 
  Grid, 
  Paper, 
  Chip,
  Button,
  CircularProgress, 
  styled
} from "@mui/material";
import { 
  LocationOn as LocationOnIcon, 
  Person as PersonIcon, 
  Phone as PhoneIcon, 
  DirectionsCar as CarIcon, 
  AccessTime as TimeIcon, 
  MonetizationOn as PriceIcon,
  Star as StarIcon
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { DirectionsService, GoogleMap, Marker, DirectionsRenderer } from "@react-google-maps/api";

const Spinner = () => {
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      height="100vh"
    >
      <CircularProgress />
    </Box>
  );
};

// Map configuration
const defaultMapOptions = {
  fullscreenControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  zoomControl: true,
  styles: [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [{ weight: "2.00" }]
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#e9e9e9" }]
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [{ color: "#f5f5f5" }]
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }]
    }
  ]
};

const MapContainer = styled('div')(({ theme }) => ({
  height: '500px',
  width: '100%',
  margin: 'auto',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: theme.shadows[6],
  border: `1px solid ${theme.palette.divider}`,
}));

const ViewReservations = () => {
  const mapRef = useRef(null);
  const [reservationData, setReservationData] = useState(null);
  const [passengerImg, setPassengerImg] = useState(null);
  const [driverImg, setDriverImg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [travelMode, setTravelMode] = useState("DRIVING");
  const [pickUpLocationLatitude, setPickUpLocationLatitude] = useState("");
  const [pickUpLocationLongitude, setPickUpLocationLongitude] = useState("");
  const [destinationLocationLatitude, setDestinationLocationLatitude] = useState("");
  const [destinationLocationLongitude, setDestinationLocationLongitude] = useState("");
  const [tripLocations, setTripLocations] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [response, setResponse] = useState(null);
  const { id } = useParams();

  // Distance calculation function
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  const directionsCallback = (response) => {
    if (!fetched && response !== null && response.status === "OK") {
      setFetched(true);
      setResponse(response);
    }
  };

  const fetchPassengerImage = async (imagePath) => {
    try {
      const response = await axios.get(`/passenger-image/${imagePath}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Failed to fetch passenger image:", error);
      return null;
    }
  };

  const fetchDriverImage = async (imagePath) => {
    try {
      const response = await axios.get(`/driver-image/${imagePath}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Failed to fetch driver image:", error);
      return null;
    }
  };

  const renderMap = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        {/* <Button
          onClick={() => setTravelMode("DRIVING")}
          variant="contained"
          color="primary"
          sx={{ mx: 1 }}
        >
          Driving Mode 
        </Button> */}
        {/* <Button
          onClick={() => setTravelMode("WALKING")}
          variant="outlined"
          color="secondary"
          sx={{ mx: 1 }}
        >
          Walking Mode
        </Button> */}
      </Box>
      <MapContainer>
        <GoogleMap
          id="direction-example"
          mapContainerStyle={{
            height: "100%",
            width: "100%",
          }}
          zoom={8}
          center={{
            lat: parseFloat(pickUpLocationLatitude) || 9.005401,
            lng: parseFloat(pickUpLocationLongitude) || 38.763611,
          }}
          options={defaultMapOptions}
        >
          <DirectionsService
            options={{
              destination: `${destinationLocationLatitude},${destinationLocationLongitude}`,
              origin: `${pickUpLocationLatitude},${pickUpLocationLongitude}`,
              travelMode: travelMode,
            }}
            callback={directionsCallback}
          />
          {response !== null && (
            <DirectionsRenderer
              options={{
                directions: response,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: "#2196F3",
                  strokeWeight: 5,
                  strokeOpacity: 0.8,
                },
              }}
            />
          )}
          <Marker
            position={{
              lat: parseFloat(pickUpLocationLatitude),
              lng: parseFloat(pickUpLocationLongitude),
            }}
            icon={{
              url: "pickup-icon.png",
              scaledSize: new window.google.maps.Size(40, 40),
            }}
          />
          <Marker
            position={{
              lat: parseFloat(destinationLocationLatitude),
              lng: parseFloat(destinationLocationLongitude),
            }}
            icon={{
              url: "dropoff-icon.png",
              scaledSize: new window.google.maps.Size(40, 40),
            }}
          />
        </GoogleMap>
      </MapContainer>
    </Box>
  );

  useEffect(() => {
    const fetchReservationDetails = async () => {
      try {
        const response = await axios.get(`/reservation/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const reservationInfo = response.data.reservation;
        setReservationData(reservationInfo);

        if (reservationInfo.passenger?.passengerImage) {
          const passengerImageUrl = await fetchPassengerImage(reservationInfo.passenger.passengerImage);
          setPassengerImg(passengerImageUrl);
        }

        if (reservationInfo.driver?.driverImage) {
          const driverImageUrl = await fetchDriverImage(reservationInfo.driver.driverImage);
          setDriverImg(driverImageUrl);
        }

        setLoading(false);
      } catch (error) {
        console.error("Reservation fetch error:", error);
        toast.error("Failed to fetch reservation details");
        setLoading(false);
      }
    };

    fetchReservationDetails();
  }, [id]);

  if (loading || !reservationData) return <Spinner />;

  const { passenger, driver, status } = reservationData;

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'warning',
      'Active': 'primary',
      'Accepted': 'success',
      'Ended': 'secondary'
    };
    return statusColors[status] || 'default';
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      padding: 3, 
      backgroundColor: '#f4f6f9',
      marginLeft: "0" // Aligning to the left
    }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar 
                    src={passengerImg || undefined} 
                    sx={{ width: 120, height: 120, mb: 2 }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6">
                    {passenger?.firstName} {passenger?.lastName}
                  </Typography>
                  <Rating 
                    value={passenger?.rating || 0} 
                    precision={0.1} 
                    readOnly 
                    icon={<StarIcon color="primary" />}
                  />
                  <Chip 
                    icon={<PhoneIcon />} 
                    label={passenger.phoneNumber} 
                    variant="outlined" 
                    sx={{ mt: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Paper>
        </Grid>

        {driver && (
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar 
                      src={driverImg || undefined} 
                      sx={{ width: 120, height: 120, mb: 2 }}
                    >
                      <CarIcon />
                    </Avatar>
                    <Typography variant="h6">
                      {driver?.firstName} {driver?.lastName}
                    </Typography>
                    <Rating 
                      value={parseFloat(driver?.rating) || 0} 
                      precision={0.1} 
                      readOnly 
                      icon={<StarIcon color="primary" />}
                    />
                    <Chip 
                      icon={<PhoneIcon />} 
                      label={driver.phoneNumber} 
                      variant="outlined" 
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Trip Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Chip 
                    icon={<LocationOnIcon />} 
                    label={`From: ${reservationData.pickUpPlace}`} 
                    color="primary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<LocationOnIcon />} 
                    label={`To: ${reservationData.destinationPlace}`} 
                    color="secondary" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<TimeIcon />} 
                    label={`Pickup: ${format(new Date(reservationData.pickUpDate), "p, dd/MM/yyyy")}`} 
                    color="info" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<PriceIcon />} 
                    label={`Price: ${parseFloat(reservationData.estimatedPrice).toFixed(2)} ETB`} 
                    color="success" 
                    variant="outlined"
                  />
                  <Badge 
                    badgeContent={status} 
                    color={getStatusColor(status)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ position: "relative", mt: 3 }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ mb: 2, fontWeight: "bold", color: "#333" }}
        >
          {/* Trip Route */}
        </Typography>
        {renderMap()}
      </Box>

      <ToastContainer />
    </Box>
  );
};

export default ViewReservations;